// 아파트 단지명(브랜드·「○○아파트」·「○○마을 N단지」) 판정과 가림 — 문의 붙여넣기 주소 파서와 시공사례 초안 가림이 한 벌을 쓴다.

// 어디에 붙어 있어도 단지명으로 보는 브랜드(일반 낱말에 섞일 일이 없는 것만).
const BRAND_ANYWHERE =
  /래미안|힐스테이트|푸르지오|e편한세상|이편한세상|아이파크|롯데캐슬|더샵|SK뷰|에스케이뷰|센트레빌|꿈에그린|아크로|디에이치|포레나|데시앙|비발디|호반써밋|스위첸|하늘채|우미린|베르디움|휴먼시아|리슈빌/i;
// 짧아서 일반 낱말(「디자이너」「주공정」)에 섞일 수 있는 브랜드는 낱말 끝(뒤에 N단지·N차 허용)일 때만.
const BRAND_TAIL = /(?:자이|위브|주공)(?:\d{1,2}(?:단지|차))?$/;
const APT_TAIL = /^[가-힣A-Za-z0-9]{2,}아파트$/;
// 이미 가린 자리표시(「OO아파트」「김OO아파트」)는 단지명이 아니다.
const PLACEHOLDER = /^[가-힣]?[OoXx0]+$/;
const VILLAGE_BLOCK = /^[가-힣]{1,8}마을\s*\d{1,2}\s*단지$/;
// 지시·위치어가 붙은 「마을」(「그마을」「옆마을」)은 단지명이 아니다 · 일반어가 붙은 「마을」(「우리마을」「고향마을」)은 뒤에 N단지·아파트·동호수가 붙을 때만 단지명이다.
const VILLAGE_DEICTIC_HEAD = ["그", "이", "저", "옆", "앞", "뒤", "뒷", "윗", "아랫", "건넛", "건너", "다른", "같은", "어느"];
const VILLAGE_COMMON_HEAD = ["우리", "저희", "이웃", "시골", "산골", "동네", "고향"];
const VILLAGE_GENERIC_HEAD = [...VILLAGE_DEICTIC_HEAD, ...VILLAGE_COMMON_HEAD];
const VILLAGE_DEICTIC = new RegExp(`^(?:${VILLAGE_DEICTIC_HEAD.join("|")})?마을$`);
const VILLAGE_GENERIC = new RegExp(`^(?:${VILLAGE_GENERIC_HEAD.join("|")})?마을$`);
const genericVillage = (w: string): boolean => VILLAGE_GENERIC.test(w);
const deicticVillage = (w: string): boolean => VILLAGE_DEICTIC.test(w);
const villageBlock = (w: string): boolean => VILLAGE_BLOCK.test(w) && !deicticVillage(w.replace(/\s*\d{1,2}\s*단지$/, ""));

// 조사가 붙은 채 온 낱말의 어간 — 「송도」「자이」「느티마을」 처럼 끝 글자가 조사와 같은 이름을 깨지 않게 뚜렷한 조사만 걷는다.
const CLEAR_PARTICLE = /(?<!마)(?:에서|에게|으로|까지|부터|은|는|을|를|에|와|과)$/;
const stemOf = (word: string): string => word.replace(CLEAR_PARTICLE, "");

// 붙여 쓴 「○○아파트」 의 앞말이 일반어·관형어·평형(「노후」「오래된」「30평대」)이면 단지명이 아니다 — 띄어 쓴 꼴과 같은 목록.
const APT_GENERIC_END = /(?:된|던|는|은|(?<!마)을|를|의)$|\d평(?:대|형)?$/;
const aptTailName = (w: string): boolean => {
  if (!APT_TAIL.test(w)) return false;
  const prefix = w.slice(0, -3);
  return (
    !PLACEHOLDER.test(prefix) && !APT_PREV_STOP.has(prefix) && !PREV_NAME_STOP.has(prefix) && !FOLLOW_STOP.has(prefix) &&
    !APT_GENERIC_END.test(prefix) && prefix !== "마을" && !deicticVillage(prefix)
  );
};

// 이름 끝 글자와 겹치는 조사(「자이」「송도」) — 떼어 낸 앞말이 단지명일 때만 조사로 본다.
const SOFT_PARTICLE = /(?:이라|인데|가|이|도|만|라)$/;
const isBrand = (w: string): boolean => BRAND_ANYWHERE.test(w) || BRAND_TAIL.test(w);
// 서술을 뗀 낱말이 브랜드거나 「브랜드아파트」(「주공아파트였는데」) — 앞 지명을 함께 가린다.
const brandish = (core: string): boolean => isBrand(core) || isBrand(core.replace(/아파트$/, ""));
const brandCore = (w: string): boolean => brandish(stemOf(w).replace(COPULA_TAIL, "")) || looseCore(w, brandish);
const anchorCore = (w: string): boolean => isBrand(w) || aptTailName(w) || villageBlock(w);
// 단지명에 붙은 서술(「은마아파트였는데」「래미안이라서」) — 떼어 낸 앞말이 단지명일 때만.
const COPULA_TAIL = /(?:였|이었|입니|이에|에요|예요|이요|이구|구요|거든|이라서|라서|이라고|라고|인데|이고|이며|임|요)[가-힣]{0,4}$/;
// 뚜렷한 조사 밖의 조사·서술(「은마아파트로」「주공이다」) — 떼어 낸 앞말이 단지명일 때만 본다(뗀 자리마다 따로 대 본다).
const LOOSE_TAILS = [
  "으로는", "으로", "로는", "로", "의", "이랑", "랑", "하고", "에는", "에선", "에도", "에만", "엔", "처럼", "같은", "쪽", "이다", "다", "이야", "야",
  "이죠", "죠", "이네요", "네요", "이지만", "지만", "이어서", "여서", "이니까", "니까", "인가요", "일까요", "이란", "란",
];
const looseCore = (w: string, ok: (core: string) => boolean): boolean =>
  LOOSE_TAILS.some((t) => w.length > t.length + 1 && w.endsWith(t) && ok(w.slice(0, -t.length)));
// 가릴 단지명 낱말(브랜드·「○○아파트」·「○○마을N단지」) — 조사가 붙었으면 "particle" · loose 는 가림 전용(주소 파서는 끝 조사를 주소에 남긴다).
const maskAnchorOf = (word: string, loose = true): "bare" | "particle" | null => {
  const stem = stemOf(word);
  const soft = stem.replace(SOFT_PARTICLE, "");
  if (soft !== stem && anchorCore(soft)) return "particle";
  if (anchorCore(stem)) return stem === word ? "bare" : "particle";
  if (loose && looseCore(word, anchorCore)) return "particle";
  const core = word.replace(COPULA_TAIL, "");
  return core !== word && core.length >= 2 && anchorCore(core) ? "particle" : null;
};
const isMaskAnchor = (word: string): boolean => maskAnchorOf(word, false) !== null;

// 사전 밖 단지명 꼴(「행복타운」「미리내빌」) — 뒤에 동·호가 붙은 자리에서만 단지명 후보다.
const UNIT_SUFFIX_NAME = /^([가-힣A-Za-z0-9]{2,})(?:아파트|마을|타운|빌)$/;
const unitSuffixName = (w: string): boolean => {
  const prefix = UNIT_SUFFIX_NAME.exec(w)?.[1];
  return (
    prefix !== undefined && !PLACEHOLDER.test(prefix) && !APT_PREV_STOP.has(prefix) && !PREV_NAME_STOP.has(prefix) && !FOLLOW_STOP.has(prefix) &&
    !MODIFIER_STOP.has(prefix) && !APT_GENERIC_END.test(prefix) && !UNIT_ADNOMINAL_END.test(prefix)
  );
};
// 가림과 주소 파서가 같이 쓰는 사전 밖 단지명 판정 — 조사를 뗀 어간으로 본다(「행복타운에 101동」).
const unitComplexWord = (word: string): boolean => unitSuffixName(stemOf(word));
// 단지명 바로 뒤 동·호(「101동」「제3동」「B동」「가동 101호」「201호」「101-1203」, 쉼표 하나 허용 · 「3호선」 과 치수(「1800-1200」「120-180 사이즈」)는 빼려고 N-N 은 호가 붙거나 앞 3자리 이하에 공백·끝이고 치수 낱말이 안 올 때만) — sticky 로 그 자리에서만 대 본다.
const UNIT_AFTER =
  /[ \t]*(?:,[ \t]*)?(?:(?:제\s*)?\d{1,4}\s*동(?:\s*\d{1,5}\s*호)?|[A-Za-z]\s*동(?:\s*\d{1,5}\s*호)?|[가나다라마바사아자차카타파하]동\s*\d{1,5}\s*호|\d{1,5}\s*호(?!선)|\d{1,4}-\d{1,4}\s*호|\d{1,3}-\d{2,4}(?=\s|$)(?![ \t]*(?:사이즈|규격|짜리|치수|크기|mm|cm|m|MM|CM|M)(?![A-Za-z])))(?![0-9A-Za-z])/y;
const unitAfterAt = (text: string, at: number): boolean => {
  UNIT_AFTER.lastIndex = at;
  return UNIT_AFTER.test(text);
};
// 붙여 쓴 「행복타운101동」 — 동 앞 이름 부분.
const JOINED_UNIT = /^([가-힣A-Za-z0-9]*?[가-힣A-Za-z])\d{1,4}동$/;

// 주소 파서의 단지명 낱말 — 가릴 낱말에 「○○마을」 을 더한다 · followed 는 바로 뒤에 N단지·동호수가 붙은 자리(「우리마을 3단지」 도 단지명).
export const isComplexAnchor = (word: string, followed = false): boolean => {
  const stem = stemOf(word);
  if (followed && unitComplexWord(word)) return true;
  if (/^[가-힣]{2,8}마을$/.test(stem)) return !deicticVillage(stem) && (followed || !genericVillage(stem));
  return isMaskAnchor(word);
};

// 단지명 뒤에 와도 단지명의 일부가 아닌 낱말(공간·공정·때·위치) — 여기서 멈춘다.
const FOLLOW_STOP = new Set([
  "공사", "시공", "현장", "리모델링", "인테리어", "올수리", "수리", "부분", "전체", "입주", "이사", "철거", "욕실", "주방", "거실",
  "안방", "작은방", "방", "화장실", "현관", "베란다", "발코니", "도배", "장판", "마루", "타일", "확장", "샷시", "새시", "중문", "가구",
  "조명", "전기", "설비", "목공", "필름", "도장", "방수", "싱크대", "준공", "완료", "착공", "마감", "청소", "보양", "입구", "단지",
  "세대", "주민", "관리실", "관리사무소", "엘리베이터", "주차장", "경비실", "놀이터", "상가", "근처", "인근", "주변", "고객", "고객님",
  "방문", "미팅", "상담", "견적", "계약", "실측", "자재", "반입", "작업", "일정", "오전", "오후", "오늘", "내일", "어제", "사진",
  "전경", "모습", "느낌", "스타일", "컨셉", "디자인", "모던", "화이트", "우드", "평형", "타입", "구조", "복도", "계단", "창호", "벽",
  "바닥", "천장", "몰딩", "도어", "붙박이장", "신발장", "드레스룸", "팬트리", "다용도실", "세탁실", "실외기실", "알파룸", "원룸",
  "투룸", "복층", "신축", "구축", "리뉴얼", "교체", "설치", "사례", "시공사례", "입주청소",
  "거주", "거주자", "거주민", "입주민", "주인", "집주인", "세입자", "세대주", "가족", "부부", "손님", "의뢰인", "사장", "실장", "소장",
  "팀장", "대표", "담당", "담당자", "기사", "사람", "분", "댁", "집", "요청", "요청사항", "요구", "문의", "의뢰", "부탁", "희망", "예정",
  "계획", "관련", "때문", "경우", "정도", "위주", "기준", "쪽", "측", "건", "확인", "점검", "협의", "통화", "연락", "전화", "문자", "카톡",
  "답변", "승인", "결정", "선택", "변경", "추가", "취소", "보류", "진행", "시작", "종료", "누수", "결로", "곰팡이", "하자", "민원", "톤",
  "컬러", "색상", "색", "분위기", "벽지", "방충망", "창문", "문", "문짝", "욕조", "변기", "세면대", "수전", "선반", "콘센트", "스위치",
  "보일러", "배관", "도어락", "걸레받이", "줄눈", "페인트", "시트지", "수납장", "상부장", "하부장", "아일랜드", "거울", "커튼", "블라인드",
  "대리석", "강마루", "원목", "가벽", "단열", "난방", "배수", "환기", "후드", "에어컨", "붙박이", "등박스", "간접등",
]);
// 「○○ 아파트」 의 앞 낱말로 쓰이지 않는 말(지시·수식어).
const APT_PREV_STOP = new Set([
  "우리", "저희", "해당", "기존", "노후", "일반", "주변", "인근", "근처", "신축", "구축", "옆", "앞", "대단지", "소단지", "소형", "대형",
  "고층", "저층", "복도식", "계단식", "판상형", "타워형", "국민", "임대", "민간", "공공", "브랜드", "이", "그", "저", "새", "옛", "같은",
  "주상복합", "재건축", "재개발", "고급", "영구임대", "공공임대",
]);
// 「빌라나 아파트」「주택이랑 아파트」 — 주거 종류를 잇는 말은 단지명이 아니다.
const HOUSING_CONJ = /^(?:빌라|주택|단독주택|전원주택|오피스텔|상가|원룸|투룸|다세대|다가구|연립|타운하우스|집|사무실|아파트)(?:이나|나|이랑|랑|와|과|하고|도|이든|든)$/;
// 앞 낱말을 단지명에 붙여도 되는 이름(「한빛 마을 아파트」「대치 래미안」) — 일반어·수식어·서술·접속어가 아니다.
const PREV_NAME_STOP = new Set([
  ...VILLAGE_GENERIC_HEAD, "그런데", "그래서", "그리고", "하지만", "그러나", "근데", "또한", "아마", "역시", "원래", "지금", "이번", "저번",
  "다음", "최근", "작년", "올해", "내년", "이전", "예전", "먼저", "다시", "요즘", "당시", "처음", "마지막", "여기", "거기", "저기",
  "그냥", "옛날", "비싼", "싼", "예쁜", "이쁜", "큰", "그런", "이런", "저런", "어떤", "아무", "다른",
]);
// 시·도와 세 글자 이상 동·읍·면·시·군·구(「대치동」「강남구」)·두 글자 읍·면·「목동」 — 「○○ 아파트」 의 앞말이면 지명이라 남긴다(「극동」「청구」 는 단지명).
const APT_REGION = /^(?:서울|부산|대구|인천|광주|대전|울산|세종|경기|강원|충북|충남|전북|전남|경북|경남|제주|경기도|강원도|제주도|목동)$|^[가-힣]{2,}(?:동|읍|면)$|^[가-힣](?:읍|면)$|^[가-힣]{2,}(?:시|군|구)$/;
// 시·도 이름과 시·군·구·동·읍·면·리로 끝나는 행정구역은 가리지 않는다(시공사례 가림이 행정구역을 남기는 정책과 같다).
const REGION_NAME = /^(?:서울|부산|대구|인천|광주|대전|울산|세종|경기|강원|충북|충남|전북|전남|경북|경남|제주|경기도|강원도|제주도)$|[가-힣](?:시|군|구|동|읍|면|리)$/;
// 브랜드 앞에 오기 쉬운 일반어(사람·때·집) — 단지명의 앞말이 아니다.
const PREV_COMMON = new Set([
  "작업자", "한번", "두번", "우리집", "저희집", "신혼", "신혼집", "친정", "시댁", "본가", "주말", "평일", "주중", "잠깐", "매번", "항상", "보통",
  "이미", "아직", "혹시", "아까", "방금", "아침", "점심", "저녁", "새벽", "전세", "월세", "자가", "매매", "남향", "정남향", "북향", "동향",
  "서향", "탑층", "로얄층", "로열층", "꼭대기", "평수", "중형", "초고층", "나홀로", "역세권", "한강뷰", "구옥", "첫번째", "두번째", "판상",
  "소규모", "이모", "후기", "완공", "리폼", "최종", "공정",
]);
// 「구」 를 뗀 서울 자치구·신도시 이름(「강남 래미안」) — 행정구역이라 브랜드 앞에서 흡수하지 않는다.
const BARE_DISTRICT = new Set([
  "강남", "강동", "강북", "강서", "관악", "광진", "구로", "금천", "노원", "도봉", "동작", "마포", "서초", "성동", "성북", "송파", "양천",
  "용산", "은평", "종로", "중랑", "분당", "일산",
]);
// 브랜드 앞 지명(「대치」「반포」)은 두 글자 — 세 글자 이상(「김철수」「사장님」「지난주」)은 사람·때라 흡수하지 않는다.
const BRAND_PREV_MAX = 2;
const prevName = (w: string, max: number): boolean =>
  new RegExp(`^[가-힣]{2,${max}}$`).test(w) && !MODIFIER_STOP.has(w) && !PREV_NAME_STOP.has(w) && !PREV_COMMON.has(w) && !BARE_DISTRICT.has(w) && !REGION_NAME.test(w) &&
  !APT_PREV_STOP.has(w) && !FOLLOW_STOP.has(w) && !PREDICATE_END.test(w) && !HOUSING_CONJ.test(w) && !/마을$/.test(w);
// 서술어·관형 어미로 끝나면 이름이 아니다(「있는」「오래된」「했고」).
const PREDICATE_END = /(?:다|요|고|며|면|니|죠|네|된|던|한|운|는|은|(?<!마)을|를|에|엔|의|이|가|님|씨)$/;
const SUFFIX_UNIT = /^\d{1,2}(?:단지|차|블록)(?:이라|인데|가|이|도|만|라|으로|로|의|이랑|랑|하고|엔|에선|에는|처럼|쪽)?$/;
// 「3단지가」 처럼 조사가 붙은 단위 낱말에서 단지명이 끝난다.
const SUFFIX_UNIT_PARTICLE = /^\d{1,2}(?:단지|차|블록)(?:이라|인데|가|이|도|만|라|으로|로|의|이랑|랑|하고|엔|에선|에는|처럼|쪽)$/;

// 브랜드 앞뒤에 와도 단지명의 일부가 아닌 부사·관형어(「너무」「새로」「빨간」) — 브랜드만 가린다.
const MODIFIER_STOP = new Set([
  "너무", "정말", "진짜", "완전", "아주", "매우", "엄청", "되게", "꽤", "조금", "많이", "새로", "다시", "전부", "모두", "같이", "함께", "직접",
  "바로", "그대로", "기존", "제일", "가장", "특히", "일단", "우선", "빨간", "하얀", "파란", "노란", "까만", "예쁜", "이쁜", "좋은", "좋아",
  "만족", "깔끔", "깨끗", "칠함",
]);
// 목록 밖 부사(「-게」「-히」)·관형형(「새로운」「멋진」「다른」 — 「-운」 은 세 글자 이상만, 「청운」 은 이름 · 「진·른」 은 「당진」 같은 지명이 많아 낱말 목록만) — 단지명 뒤 낱말에만 쓴다.
const ADNOMINAL_END = /(?:[가-힣]{2}운|던|된|한|쁜|픈|긴|란|간|큰)$/;
const ADNOMINAL_WORDS = new Set(["멋진", "다른", "푸른", "빠른", "바른", "이른", "고른", "어진", "못난", "잘난", "지친", "거친", "뻗친", "마른", "부른", "모진", "다진", "그른", "누른"]);
const modifierWord = (w: string): boolean => MODIFIER_STOP.has(w) || ADNOMINAL_WORDS.has(w) || /(?:게|히)$/.test(w) || ADNOMINAL_END.test(w);
// 동·호 근거가 있는 이름의 앞말은 뚜렷한 활용형(「새로운」「깨끗한」)만 관형어로 본다 — 「한진」「대한」「푸른」「삼진」 은 이름.
const UNIT_ADNOMINAL_END = /[가-힣]{2}(?:운|한)$/;

// 단지명 뒤에 이어 붙일 수 있는 낱말 — 지명·외래어 수식어(「대치」「팰리스」「더」「3단지」).
const followable = (word: string): boolean => {
  const stem = stemOf(word);
  // 「욕실도」「벽지만」 처럼 겹치는 조사가 붙은 공정어도 멈춘다 — 「송도」 를 깨지 않게 목록 대조에만 뗀다.
  if (SUFFIX_UNIT.test(word)) return true;
  if (modifierWord(stem)) return false;
  if (FOLLOW_STOP.has(stem.replace(SOFT_PARTICLE, "")) || FOLLOW_STOP.has(stem) || /(?:다|요|고|며|면|니|죠|네|된|던|하는|하던|했던|님|씨)$/.test(stem)) return false;
  // 「고객의」「입주민께서」 처럼 관형격·높임 조사가 붙은 낱말은 사람·일을 가리킨다.
  if (/(?:의|께서|께|이랑|랑|하고)$/.test(word)) return false;
  if (SUFFIX_UNIT.test(stem)) return true;
  return /^[가-힣A-Za-z]{2,10}$/.test(stem) || stem === "더";
};

// 단지명 뒤 이름에 붙은 뚜렷한 서술(「래미안 퍼스티지였어요」) — 뗀 앞말이 이어 붙일 낱말이면 거기까지 가리고 끝낸다.
const FOLLOW_COPULA = /(?:였|이었|이에요|예요|이라서|이구요|인데)[가-힣]{0,4}$/;
const copulaFollow = (word: string): boolean => {
  const core = word.replace(FOLLOW_COPULA, "");
  return core !== word && core.length >= 2 && followable(core);
};

interface Token {
  start: number;
  end: number;
  word: string;
}

const FOLLOW_MAX = 3;

// 단지명을 뒤 조사까지 걷는다 — 브랜드 뒤 지명·수식어는 3낱말까지 함께, 「○○ 아파트」 는 앞 낱말까지.
export const maskApartmentComplex = (text: string): string => {
  const tokens: Token[] = Array.from(text.matchAll(/[가-힣A-Za-z0-9]+/g), (m) => ({
    start: m.index ?? 0,
    end: (m.index ?? 0) + m[0].length,
    word: m[0],
  }));
  const spaced = (a: Token, b: Token): boolean => /^[ \t]+$/.test(text.slice(a.end, b.start));
  const ranges: [number, number][] = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i];
    let from = i;
    let to = i;
    const anchor = maskAnchorOf(t.word);
    let ended = anchor === "particle";
    const free = (k: number): boolean => k >= 0 && (ranges.length === 0 || ranges[ranges.length - 1][1] <= tokens[k].start);
    const next1 = tokens[i + 1];
    const next2 = tokens[i + 2];
    const joined = anchor === null ? JOINED_UNIT.exec(t.word)?.[1] : undefined;
    if (joined !== undefined && unitSuffixName(joined)) {
      ranges.push([t.start, t.start + joined.length]);
      continue;
    }
    if (anchor === null) {
      const unitAt = (k: number): boolean => tokens[k] !== undefined && spaced(tokens[k - 1], tokens[k]) && /^\d{1,2}단지/.test(tokens[k].word);
      const village = next1 !== undefined && /^[가-힣]{1,8}마을$/.test(t.word) && t.word !== "마을" && unitAt(i + 1);
      // 「느티 마을 3단지」 — 띄어 쓴 「마을」 앞 이름까지 한 단지명.
      const spacedVillage = next1 !== undefined && next1.word === "마을" && spaced(t, next1) && prevName(t.word, 8) && unitAt(i + 2);
      // 「은마 아파트였는데」 처럼 서술이 붙은 「아파트」 도 같다.
      const aptWord = stemOf(t.word).replace(SOFT_PARTICLE, "") === "아파트" || t.word.replace(COPULA_TAIL, "") === "아파트" || looseCore(t.word, (c) => c === "아파트");
      const aptPrev = aptWord && i > 0 && spaced(tokens[i - 1], t) ? tokens[i - 1].word : null;
      const aptOk =
        aptPrev !== null && /^[가-힣A-Za-z]{2,10}$/.test(aptPrev) && !APT_PREV_STOP.has(aptPrev) && !PREV_NAME_STOP.has(aptPrev) &&
        !PREV_COMMON.has(aptPrev) && !CLEAR_PARTICLE.test(aptPrev) &&
        !APT_REGION.test(aptPrev) && !PLACEHOLDER.test(aptPrev) && !FOLLOW_STOP.has(aptPrev) && (!PREDICATE_END.test(aptPrev) || unitSuffixName(aptPrev)) &&
        aptPrev !== "마을" && !HOUSING_CONJ.test(aptPrev);
      // 「한빛 마을아파트였는데」 — 붙여 쓴 「마을아파트」 도 앞 이름까지.
      const villageAptWord =
        stemOf(t.word).replace(SOFT_PARTICLE, "") === "마을아파트" || t.word.replace(COPULA_TAIL, "") === "마을아파트" || looseCore(t.word, (c) => c === "마을아파트");
      const joinedVillageApt = villageAptWord && i > 0 && spaced(tokens[i - 1], t) && prevName(tokens[i - 1].word, 8);
      // 「한빛 마을 아파트」 — 띄어 쓴 「마을」 은 앞 이름이 있을 때만 단지명이다(「그 마을 아파트」 는 아님).
      const villageApt = aptPrev === "마을" && i > 1 && spaced(tokens[i - 2], tokens[i - 1]) && prevName(tokens[i - 2].word, 8);
      if (village) from = i;
      else if (unitComplexWord(t.word) && unitAfterAt(text, t.end)) ended = true;
      else if (spacedVillage && next2 !== undefined) {
        to = i + 2;
        ended = SUFFIX_UNIT_PARTICLE.test(next2.word);
      } else if (aptOk && free(i - 1)) {
        from = i - 1;
        ended = t.word !== "아파트";
      } else if (villageApt && free(i - 2)) {
        from = i - 2;
        ended = t.word !== "아파트";
      } else if (joinedVillageApt && free(i - 1)) {
        from = i - 1;
        ended = t.word !== "마을아파트";
      } else continue;
    } else if (brandCore(t.word) && i > 0 && spaced(tokens[i - 1], t) && prevName(tokens[i - 1].word, BRAND_PREV_MAX) && free(i - 1)) {
      // 「대치 래미안」 — 브랜드 앞 지명까지 가린다.
      from = i - 1;
    }
    // 조사가 붙은 낱말(「래미안에서」「은마아파트가」「3단지가」)에서 단지명이 끝난다.
    for (let k = 0; k < FOLLOW_MAX && !ended && !CLEAR_PARTICLE.test(tokens[to].word); k += 1) {
      const next = tokens[to + 1];
      if (!next || !spaced(tokens[to], next)) break;
      const plain = followable(next.word);
      if (!plain && !copulaFollow(next.word)) break;
      to += 1;
      ended = !plain || SUFFIX_UNIT_PARTICLE.test(next.word);
    }
    ranges.push([tokens[from].start, tokens[to].end]);
    i = to;
  }
  let out = "";
  let last = 0;
  for (const [s, e] of ranges) {
    out += text.slice(last, s);
    last = e;
  }
  return out + text.slice(last);
};
